
import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Mail, MapPin, Facebook, Instagram, Twitter, Clock, Lock, Globe, FileSpreadsheet } from 'lucide-react';
import { excelService } from '../services/excelService';
import { Logo } from './Logo';

export const Navbar: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  const isActive = (path: string) => location.pathname === path;
  
  const linkClass = (path: string) => `px-4 py-8 text-sm font-bold uppercase tracking-widest transition-all border-b-4 h-full flex items-center ${
    isActive(path) 
      ? 'text-primary border-primary' 
      : 'text-brandDark border-transparent hover:text-primary hover:border-accent'
  }`;

  const mobileLinkClass = (path: string) => `block px-4 py-4 text-base font-bold uppercase tracking-widest transition-colors ${
    isActive(path) ? 'text-primary bg-brandGray' : 'text-brandDark hover:bg-brandGray'
  }`;

  return (
    <header className="w-full z-50 shadow-md sticky top-0 bg-white">
      {/* Top Bar - Contact Info */}
      <div className="bg-brandDark text-white py-2.5 text-[11px] font-bold uppercase tracking-widest">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center">
          <div className="flex space-x-8">
            <a href="tel:7738639999" className="flex items-center gap-2 hover:text-accent transition-colors whitespace-nowrap">
              <Phone size={14} className="text-accent" /> +1 773-863-9999
            </a>
            <span className="hidden sm:flex items-center gap-2">
              <MapPin size={14} className="text-danger" /> 6115 S Ashland Ave, Chicago
            </span>
          </div>
          <div className="flex items-center space-x-6">
            <span className="hidden md:flex items-center gap-2">
              <Clock size={14} className="text-primary" /> Mon-Sat: 9am - 7pm
            </span>
            <div className="flex space-x-4 md:border-l md:border-gray-700 md:pl-4">
              <Facebook size={14} className="cursor-pointer hover:text-primary transition-colors" />
              <Instagram size={14} className="cursor-pointer hover:text-primary transition-colors" />
            </div>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <nav className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-28">
            {/* Logo Section */}
            <div className="flex-shrink-0 py-2">
              <Link to="/" className="block group">
                <Logo size="lg" variant="dark" className="transform group-hover:scale-[1.02] transition-transform duration-300" />
              </Link>
            </div>
            
            {/* Desktop Menu */}
            <div className="hidden lg:block h-full">
              <div className="flex items-center h-full">
                <Link to="/" className={linkClass('/')}>Home</Link>
                <Link to="/inventory" className={linkClass('/inventory')}>Inventory</Link>
                <Link to="/request" className={linkClass('/request')}>Request a Car</Link>
                <Link to="/about" className={linkClass('/about')}>About Us</Link>
                <Link to="/contact" className={linkClass('/contact')}>Contact</Link>
              </div>
            </div>

            {/* Mobile Menu Button */}
            <div className="flex lg:hidden">
              <button
                onClick={() => setIsOpen(!isOpen)}
                className="inline-flex items-center justify-center p-3 rounded-md text-brandDark hover:text-primary transition-colors focus:outline-none"
              >
                {isOpen ? <X size={32} /> : <Menu size={32} />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu dropdown */}
        {isOpen && (
          <div className="lg:hidden border-t border-brandGray bg-white shadow-2xl animate-fade-in">
            <div className="px-2 pt-2 pb-4 space-y-1">
              <Link to="/" onClick={() => setIsOpen(false)} className={mobileLinkClass('/')}>Home</Link>
              <Link to="/inventory" onClick={() => setIsOpen(false)} className={mobileLinkClass('/inventory')}>Inventory</Link>
              <Link to="/request" onClick={() => setIsOpen(false)} className={mobileLinkClass('/request')}>Request a Car</Link>
              <Link to="/about" onClick={() => setIsOpen(false)} className={mobileLinkClass('/about')}>About Us</Link>
              <Link to="/contact" onClick={() => setIsOpen(false)} className={mobileLinkClass('/contact')}>Contact</Link>
            </div>
            {/* Mobile Contact Quick Access */}
            <div className="px-6 py-5 bg-brandGray border-t border-gray-100 flex flex-col gap-3">
              <a href="tel:7738639999" className="flex items-center gap-3 text-brandDark font-bold hover:text-primary transition-colors text-sm py-1.5 whitespace-nowrap">
                <Phone size={16} className="text-primary" /> +1 773-863-9999
              </a>
              <div className="flex items-start gap-3 text-gray-500 text-xs py-0.5">
                <MapPin size={16} className="text-danger flex-shrink-0 mt-0.5" />
                <span>6115 S Ashland Ave, Chicago, IL</span>
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Senegal Flag Accent Stripe */}
      <div className="flex h-1.5 w-full shadow-sm">
        <div className="bg-primary flex-1"></div>
        <div className="bg-accent flex-1"></div>
        <div className="bg-danger flex-1"></div>
      </div>
    </header>
  );
};

export const Footer: React.FC = () => {
  return (
    <footer className="bg-brandDark text-white pt-20 pb-10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
          <div className="flex flex-col items-center md:items-start">
            <div className="mb-8 w-fit">
              <Logo variant="light" size="lg" />
            </div>
            <p className="text-gray-400 text-sm leading-relaxed mb-8 text-center md:text-left">
              Chicago's most trusted source for high-quality, pre-owned vehicles. We specialize in luxury, performance, and family cars with a global standard of service.
            </p>
            <div className="flex space-x-5">
              <a href="#" className="h-11 w-11 bg-brandDark border border-gray-700 rounded-full flex items-center justify-center hover:bg-primary hover:border-primary transition-all duration-300"><Facebook size={20} /></a>
              <a href="#" className="h-11 w-11 bg-brandDark border border-gray-700 rounded-full flex items-center justify-center hover:bg-primary hover:border-primary transition-all duration-300"><Instagram size={20} /></a>
              <a href="#" className="h-11 w-11 bg-brandDark border border-gray-700 rounded-full flex items-center justify-center hover:bg-primary hover:border-primary transition-all duration-300"><Twitter size={20} /></a>
            </div>
          </div>

          <div>
            <h3 className="font-heading text-xl font-bold uppercase mb-8 flex items-center gap-3">
              <span className="w-1.5 h-7 bg-primary"></span> Our Inventory
            </h3>
            <ul className="space-y-4 text-gray-400 text-sm font-medium">
              <li><Link to="/inventory" className="hover:text-primary transition-colors">All Vehicles</Link></li>
              <li><Link to="/inventory" className="hover:text-primary transition-colors">Sedans Collection</Link></li>
              <li><Link to="/inventory" className="hover:text-primary transition-colors">SUVs & Family Trucks</Link></li>
              <li><Link to="/inventory" className="hover:text-primary transition-colors">Luxury & Sport</Link></li>
              <li><Link to="/inventory" className="hover:text-primary transition-colors">Newly Added</Link></li>
            </ul>
          </div>

          <div>
            <h3 className="font-heading text-xl font-bold uppercase mb-8 flex items-center gap-3">
              <span className="w-1.5 h-7 bg-accent"></span> Global Network
            </h3>
            <ul className="space-y-4 text-gray-400 text-sm font-medium">
              <li className="flex items-start gap-3">
                 <Globe size={16} className="text-primary mt-1" />
                 <span>Chicago HQ - Main Hub</span>
              </li>
              <li className="flex items-start gap-3">
                 <Globe size={16} className="text-accent mt-1" />
                 <span>Dakar Branch - West Africa</span>
              </li>
              <li><Link to="/about" className="hover:text-primary transition-colors ml-7">Our Global Story</Link></li>
              <li><Link to="/request" className="hover:text-primary transition-colors ml-7">International Export</Link></li>
            </ul>
          </div>

          <div>
            <h3 className="font-heading text-xl font-bold uppercase mb-8 flex items-center gap-3">
              <span className="w-1.5 h-7 bg-danger"></span> Global Locations
            </h3>
            <ul className="space-y-6 text-gray-400 text-sm">
              <li className="flex items-start gap-4">
                <MapPin size={20} className="text-primary flex-shrink-0 mt-1" />
                <span className="leading-relaxed"><strong className="text-white block mb-1">CHICAGO HUB</strong> 6115 S Ashland Ave,<br />Chicago, IL 60636, USA</span>
              </li>
              <li className="flex items-start gap-4">
                <MapPin size={20} className="text-accent flex-shrink-0 mt-1" />
                <span className="leading-relaxed"><strong className="text-white block mb-1">DAKAR BRANCH</strong> 334 Rue MZ 26,<br />Dakar, Senegal</span>
              </li>
              <li className="flex flex-col gap-3 pt-2 border-t border-gray-800">
                <div className="flex items-center gap-4">
                  <Phone size={20} className="text-primary flex-shrink-0" />
                  <span className="font-bold text-white">+1 773-863-9999</span>
                </div>
                <div className="flex items-center gap-4">
                  <Mail size={20} className="text-danger flex-shrink-0" />
                  <div className="flex flex-col text-[10px] uppercase font-bold tracking-tighter">
                    <a href="mailto:info@dakarautosales.com" className="hover:text-white transition-colors">USA: info@dakarautosales.com</a>
                    <a href="mailto:info.senegal@dakarautosales.com" className="hover:text-white transition-colors">Dakar: info.senegal@dakarautosales.com</a>
                  </div>
                </div>
              </li>
            </ul>
          </div>
        </div>
        
        <div className="pt-10 border-t border-gray-800 flex flex-col md:flex-row justify-between items-center gap-6 text-[10px] text-gray-500 uppercase tracking-wider font-bold">
          <div className="text-center md:text-left tracking-[0.15em]">
            &copy; {new Date().getFullYear()} Dakar Auto Sales Inc. | Premium Pre-Owned Automotive | All Rights Reserved
          </div>
          <div className="flex flex-wrap justify-center items-center gap-6">
            <Link to="/admin" className="text-gray-400 hover:text-primary transition-colors flex items-center gap-1.5 uppercase font-bold text-[10px] tracking-wider">
              <Lock size={12} className="text-primary" /> Staff Portal
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <div className="min-h-screen flex flex-col font-sans bg-white">
      <Navbar />
      <main className="flex-grow">
        {children}
      </main>
      <Footer />
    </div>
  );
};
